/**
 * Fix 2 (WP-577 review round 3, TEN-2918 follow-up): the single-capture `preview: true` branch
 * (tools/smart-capture.ts, the `isPreviewCreateEntryResult(result)` branch inside `handleCapture`)
 * has returned structured data — `entryId`, `name`, `collection`, `outcome`, `blockingReason`,
 * `warnings`, `groundingReport`, `taskAlignment` — since WP-316 S3 / TEN-2458. That branch was
 * only reachable in practice once PR #523 closed the action/mutation validator parity gap for
 * `preview: true` (before that, a preview call threw at the gateway before reaching this code).
 * `captureOutputSchema` (tools/smart-capture.ts) was a union of only the strict real-capture
 * shape (`status` required) and the classifier-only fallback shape — neither accepts this
 * branch's object, so any consumer validating a successful single-capture preview response
 * against the documented `envelopeSchema(captureOutputSchema)` contract rejected it outright.
 *
 * This schema is derived directly from the object the branch actually builds (smart-capture.ts,
 * the `success(...)` call inside the preview branch) — not guessed. `contract` is deliberately
 * NOT declared here: it rides the envelope's own top-level `contract` field
 * (envelope.ts's `envelopeSchema`), never `data`.
 *
 * Extracted to lib/ (rather than declared inline in smart-capture.ts) for the same STD-2 /
 * DEC-1504 shrink-only reason as `batchCaptureOutputSchema` (lib/batchCaptureOutput.ts) — that
 * file is grandfathered over-budget and may not grow.
 */
// zod/v3 — must match smart-capture.ts's specifier exactly; a bare "zod" import resolves to v4
// here and the two instances' types are not assignable across a z.union() (same note as
// lib/batchCaptureOutput.ts).
import { z } from "zod/v3";

/** One `groundingReport.related` entry (smart-capture.ts's `groundingRelated` mapping). */
const groundingRelatedEntrySchema = z.object({
  entryId: z.string(),
  name: z.string(),
  collectionSlug: z.string(),
  overlapRatio: z.number(),
  recommendedRelationType: z.string(),
  reasoning: z.string(),
});

/** One `groundingReport.duplicates` entry (smart-capture.ts's `groundingDuplicates` mapping). */
const groundingDuplicateEntrySchema = z.object({
  entryId: z.string(),
  name: z.string(),
  collectionSlug: z.string(),
  matchType: z.enum(["related", "possible_duplicate"]),
  overlapRatio: z.number(),
});

/** One `groundingReport.governance` entry (smart-capture.ts's `groundingGovernance`). */
const groundingGovernanceEntrySchema = z.object({
  entryId: z.string(),
  name: z.string(),
  collectionSlug: z.string(),
});

/**
 * The pre-write grounding report always included on a capture response (WP-318 S2) — every
 * array is required (present, possibly empty), matching how smart-capture.ts always spreads
 * `groundingReport` unconditionally rather than gating it on any of the three cohorts being
 * non-empty.
 */
const groundingReportSchema = z.object({
  related: z.array(groundingRelatedEntrySchema),
  duplicates: z.array(groundingDuplicateEntrySchema),
  governance: z.array(groundingGovernanceEntrySchema),
});

/**
 * The kernel's librarian task-alignment verdict (convex/lib/gates/taskAlignmentPolicy.ts's
 * `OrientTaskAlignment`) — loose record, same single-source-of-truth rationale already used for
 * `coherencyRefusal`/`contradictionAdvisory`/`qualityVerdict` in `captureSuccessOutputSchema`
 * (tools/smart-capture.ts): the kernel owns this shape, re-declaring it field-for-field here
 * would create a second source of truth that silently drifts.
 */
const taskAlignmentSchema = z.record(z.unknown());

/**
 * The single `capture` tool's `preview: true` response data — a THIRD member of the output
 * union, distinct from both the real-create success shape (`status` required) and the
 * classifier-only fallback shape (`classifier` only). No DB write happens on this branch, so it
 * carries no `status` at all — reporting one would be the same "preview claims a write occurred"
 * lie `lib/batchCaptureOutput.ts`'s doc comment describes for the batch path.
 */
export const captureSinglePreviewOutputSchema = z.object({
  entryId: z.string(),
  name: z.string(),
  collection: z.string(),
  outcome: z.enum(["preview", "blocked"]),
  // Present only when `outcome: "blocked"` AND the server supplied a reason.
  blockingReason: z.string().optional(),
  // Always present — `result.warnings ?? []`, never omitted.
  warnings: z.array(z.string()),
  groundingReport: groundingReportSchema,
  // TEN-2458: only present when the kernel computed a librarian verdict for this preview.
  taskAlignment: taskAlignmentSchema.optional(),
}).strict();
